import { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded";

import ActionHeaderCard from "../components/General/ActionHeaderCard";
import {
  getOperationTypes,
  updateSentenceMarkers,
} from "../services/OperationTypeService";
import {
  getLiquidationItemTypes,
  updateSentenceRubros,
  updateExecutionRubros,
} from "../services/LiquidationService";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const CATEGORY_LABEL = {
  honorarios: "Honorarios",
  gastos: "Gastos",
  capital: "Capital",
};

function ConfigSection({
  title,
  description,
  loading,
  saving,
  canManage,
  options,
  selected,
  onChange,
  onSave,
  getOptionLabel,
  emptyHint,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2.5,
        borderRadius: 3,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, opacity: 0.7 }}>{description}</Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={options}
          value={selected}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          getOptionLabel={getOptionLabel}
          onChange={(_, newValue) => onChange(newValue)}
          renderOption={(props, option, { selected: isSelected }) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={isSelected}
                  size="small"
                  sx={{ mr: 1 }}
                />
                {getOptionLabel(option)}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label={emptyHint} placeholder="Buscar…" />
          )}
        />
      )}

      <Typography sx={{ fontSize: 12.5, opacity: 0.6 }}>
        {selected.length} seleccionado{selected.length === 1 ? "" : "s"}.
      </Typography>

      <Button
        variant="contained"
        onClick={onSave}
        disabled={!canManage || saving || loading}
        sx={{ alignSelf: "flex-start", borderRadius: 2, fontWeight: 800 }}
      >
        {saving ? <CircularProgress size={18} color="inherit" /> : "Guardar"}
      </Button>
    </Box>
  );
}

export default function SettingsSentenceMarkers() {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission("administrar marcadores sentencia");

  const [operationTypes, setOperationTypes] = useState([]);
  const [selectedOperationTypes, setSelectedOperationTypes] = useState([]);
  const [loadingOperationTypes, setLoadingOperationTypes] = useState(true);
  const [savingOperationTypes, setSavingOperationTypes] = useState(false);

  const [itemTypes, setItemTypes] = useState([]);
  const [selectedSentenceRubros, setSelectedSentenceRubros] = useState([]);
  const [selectedExecutionRubros, setSelectedExecutionRubros] = useState([]);
  const [loadingItemTypes, setLoadingItemTypes] = useState(true);
  const [savingSentenceRubros, setSavingSentenceRubros] = useState(false);
  const [savingExecutionRubros, setSavingExecutionRubros] = useState(false);

  const loadOperationTypes = useCallback(async () => {
    setLoadingOperationTypes(true);
    try {
      const res = await getOperationTypes();
      const all = res.data?.data || [];
      setOperationTypes(all);
      setSelectedOperationTypes(all.filter((o) => o.marks_sentence));
    } catch {
      showToast("No se pudieron cargar los tipos de operación.", "error");
    } finally {
      setLoadingOperationTypes(false);
    }
  }, [showToast]);

  const loadItemTypes = useCallback(async () => {
    setLoadingItemTypes(true);
    try {
      const res = await getLiquidationItemTypes({ perPage: 1000 });
      const all = res.data?.data || [];
      setItemTypes(all);
      setSelectedSentenceRubros(all.filter((t) => t.counts_as_sentence_honorarios));
      setSelectedExecutionRubros(all.filter((t) => t.counts_as_execution_honorarios));
    } catch {
      showToast("No se pudieron cargar los rubros de liquidación.", "error");
    } finally {
      setLoadingItemTypes(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadOperationTypes();
    loadItemTypes();
  }, [loadOperationTypes, loadItemTypes]);

  const handleSaveOperationTypes = async () => {
    setSavingOperationTypes(true);
    try {
      await updateSentenceMarkers(selectedOperationTypes.map((o) => o.id));
      showToast("Operaciones de sentencia guardadas.", "success");
      await loadOperationTypes();
    } catch (e) {
      showToast(
        e?.response?.data?.message || "No se pudieron guardar las operaciones.",
        "error",
      );
    } finally {
      setSavingOperationTypes(false);
    }
  };

  const handleSaveSentenceRubros = async () => {
    setSavingSentenceRubros(true);
    try {
      await updateSentenceRubros(selectedSentenceRubros.map((t) => t.id));
      showToast("Rubros de Sentencia guardados.", "success");
      await loadItemTypes();
    } catch (e) {
      showToast(
        e?.response?.data?.message || "No se pudieron guardar los rubros.",
        "error",
      );
    } finally {
      setSavingSentenceRubros(false);
    }
  };

  const handleSaveExecutionRubros = async () => {
    setSavingExecutionRubros(true);
    try {
      await updateExecutionRubros(selectedExecutionRubros.map((t) => t.id));
      showToast("Rubros de Ejecución guardados.", "success");
      await loadItemTypes();
    } catch (e) {
      showToast(
        e?.response?.data?.message || "No se pudieron guardar los rubros.",
        "error",
      );
    } finally {
      setSavingExecutionRubros(false);
    }
  };

  const itemTypeLabel = (option) =>
    `${option.name} (${CATEGORY_LABEL[option.category] || option.category})`;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<BalanceRoundedIcon />}
            title="Marcadores de Sentencia Municipalidad"
            description="Configuración usada por la Planilla Municipalidad: qué operaciones marcan que un expediente tiene sentencia, y qué rubros de la liquidación activa se suman en cada columna de honorarios."
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ConfigSection
            title="Operaciones que marcan Sentencia"
            description='La "Fecha de Sentencia" de la planilla se toma de la operación más antigua (la primera que se encuentre) entre estos tipos, dentro de cada expediente.'
            loading={loadingOperationTypes}
            saving={savingOperationTypes}
            canManage={canManage}
            options={operationTypes}
            selected={selectedOperationTypes}
            onChange={setSelectedOperationTypes}
            onSave={handleSaveOperationTypes}
            getOptionLabel={(option) => option.name}
            emptyHint="Tipos de operación que marcan sentencia"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ConfigSection
            title='Rubros que suman en "Sentencia" (Honorarios)'
            description="De la liquidación activa del expediente, estos rubros se suman en la columna Honorarios de Sentencia."
            loading={loadingItemTypes}
            saving={savingSentenceRubros}
            canManage={canManage}
            options={itemTypes}
            selected={selectedSentenceRubros}
            onChange={setSelectedSentenceRubros}
            onSave={handleSaveSentenceRubros}
            getOptionLabel={itemTypeLabel}
            emptyHint="Rubros de liquidación"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ConfigSection
            title='Rubros que suman en "Ejecución" (Honorarios)'
            description="De la liquidación activa del expediente, estos rubros se suman en la columna Honorarios de Ejecución."
            loading={loadingItemTypes}
            saving={savingExecutionRubros}
            canManage={canManage}
            options={itemTypes}
            selected={selectedExecutionRubros}
            onChange={setSelectedExecutionRubros}
            onSave={handleSaveExecutionRubros}
            getOptionLabel={itemTypeLabel}
            emptyHint="Rubros de liquidación"
          />
        </Grid>
      </Grid>
    </Container>
  );
}
